import { Plus, Trash2 } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { TEMPLATE_FIELDS_MAX, TEMPLATE_ICONS, renderTemplate, templateInputError, templatePrompt } from '@orgus/protocol';
import type { MissionTemplate, TemplateField, TemplateIcon, TemplateInput } from '@orgus/protocol';
import { Button, Dialog, ErrorState, IconButton, Input, Segmented, Select, Textarea, toast, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { saveTemplate } from './client.ts';
import { TEMPLATE_ICON_LABEL } from './TemplateIcon.tsx';

interface Draft {
  title: string;
  summary: string;
  icon: TemplateIcon;
  missionTitle: string;
  goal: string;
  prompt: string;
  branch: string;
  mode: 'headless' | 'terminal';
  fields: TemplateField[];
}

const EMPTY: Draft = { title: '', summary: '', icon: 'sparkles', missionTitle: '', goal: '', prompt: '', branch: '', mode: 'headless', fields: [] };

function fromTemplate(t: MissionTemplate, copy: boolean): Draft {
  return {
    title: copy ? `${t.title} (copy)` : t.title,
    summary: t.summary,
    icon: t.icon,
    // Empty follows the title; an older template that copied it follows it too.
    missionTitle: t.missionTitle === t.title ? '' : t.missionTitle,
    goal: t.goal,
    prompt: t.prompt,
    branch: t.branch ?? '',
    mode: t.mode,
    fields: t.fields.map((f) => ({ ...f })),
  };
}

export function toInput(d: Draft): TemplateInput {
  return {
    title: d.title.trim(),
    summary: d.summary.trim(),
    icon: d.icon,
    missionTitle: d.missionTitle.trim(),
    goal: d.goal.trim(),
    prompt: d.prompt.trim(),
    branch: d.branch.trim() || null,
    mode: d.mode,
    fields: d.fields.map((f) => ({ ...f, key: f.key.trim(), label: f.label.trim() })),
  };
}

/** Add, change or duplicate a mission template. `base` with `copy` duplicates; without it edits. */
export function TemplateDialog({ open, onOpenChange, base, copy }: { open: boolean; onOpenChange: (v: boolean) => void; base: MissionTemplate | null; copy: boolean }) {
  const [d, setD] = useState<Draft>(EMPTY);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const editing = !!base && !copy;

  useEffect(() => {
    if (!open) return;
    setD(base ? fromTemplate(base, copy) : EMPTY);
    setTouched(false);
    setBusy(false);
    setError(null);
  }, [open, base, copy]);

  const input = useMemo(() => toInput(d), [d]);
  const invalid = templateInputError(input);
  const preview = useMemo(() => {
    if (invalid) return null;
    const sample: Record<string, string> = {};
    for (const f of input.fields ?? []) sample[f.key] = f.placeholder || f.label;
    const now = Date.now();
    return templatePrompt(
      renderTemplate({ ...input, id: 'preview', summary: input.summary ?? '', icon: input.icon ?? 'sparkles', missionTitle: input.missionTitle || input.title, prompt: input.prompt ?? '', fields: input.fields ?? [], mode: input.mode ?? 'headless', branch: input.branch ?? null, tags: [], builtIn: false, createdBy: null, createdAt: now, updatedAt: now }, sample),
    );
  }, [input, invalid]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));
  const setField = (i: number, patch: Partial<TemplateField>) => setD((x) => ({ ...x, fields: x.fields.map((f, j) => (j === i ? { ...f, ...patch } : f)) }));

  const save = async () => {
    setTouched(true);
    if (invalid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const t = await saveTemplate(input, editing ? base!.id : undefined);
      toast.success(editing ? 'Template saved' : 'Template added', `${t.title} is offered when starting a mission.`);
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={editing ? `Edit ${base!.title}` : copy ? `Duplicate ${base!.title}` : 'Add a mission template'}
      description="Write the goal and the instructions once; people fill in the fields when they start a mission. Use {key} to put a field's value in the text."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={save}>
            {editing ? 'Save template' : 'Add template'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_180px]">
          <Input label="Title" placeholder="Lint a folder" value={d.title} onChange={(e) => set('title', e.target.value)} />
          <Select label="Icon" value={d.icon} onValueChange={(v) => set('icon', v as TemplateIcon)} options={TEMPLATE_ICONS.map((i) => ({ value: i, label: TEMPLATE_ICON_LABEL[i] }))} />
        </div>
        <Input label="Summary" hint="One line on the card." placeholder="Fix every lint error in one folder." value={d.summary} onChange={(e) => set('summary', e.target.value)} />
        <Input label="Mission title" hint="Leave empty to use the title. May use fields, for example Lint {dir}." placeholder={d.title.trim() || undefined} value={d.missionTitle} onChange={(e) => set('missionTitle', e.target.value)} />
        <Textarea label="Goal" hint="What done looks like." rows={2} value={d.goal} onChange={(e) => set('goal', e.target.value)} />
        <Textarea label="Instructions for the agent" rows={5} value={d.prompt} onChange={(e) => set('prompt', e.target.value)} />

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="flex-1 text-caption font-medium text-fg-muted">Fields</span>
            <Button
              size="sm"
              variant="ghost"
              icon={<Plus />}
              disabled={d.fields.length >= TEMPLATE_FIELDS_MAX}
              onClick={() => set('fields', [...d.fields, { key: '', label: '', required: true }])}
            >
              Add field
            </Button>
          </div>
          {d.fields.length === 0 ? (
            <p className="rounded-control border border-dashed border-border px-3 py-3 text-caption text-fg-subtle">No fields: the mission starts as written. Add one for anything that changes per mission, like a folder.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {d.fields.map((f, i) => (
                <li key={i} className="grid items-end gap-2 md:grid-cols-[120px_minmax(0,1fr)_minmax(0,1fr)_120px_auto]">
                  <Input size="sm" label={i === 0 ? 'Key' : undefined} aria-label="Key" placeholder="dir" value={f.key} onChange={(e) => setField(i, { key: e.target.value })} />
                  <Input size="sm" label={i === 0 ? 'Label' : undefined} aria-label="Label" placeholder="Folder" value={f.label} onChange={(e) => setField(i, { label: e.target.value })} />
                  <Input size="sm" label={i === 0 ? 'Example' : undefined} aria-label="Example" placeholder="src/api" value={f.placeholder ?? ''} onChange={(e) => setField(i, { placeholder: e.target.value || undefined })} />
                  <Select
                    size="sm"
                    label={i === 0 ? 'Needed' : undefined}
                    aria-label="Needed"
                    value={f.required ? 'yes' : 'no'}
                    onValueChange={(v) => setField(i, { required: v === 'yes' })}
                    options={[
                      { value: 'yes', label: 'Required' },
                      { value: 'no', label: 'Optional' },
                    ]}
                  />
                  <IconButton label={`Remove field ${f.label || i + 1}`} icon={<Trash2 />} size="sm" onClick={() => set('fields', d.fields.filter((_, j) => j !== i))} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <Input wrapperClassName="min-w-56 flex-1" label="Branch for a new worktree" hint="Optional. May use fields: chore/lint-{dir}." value={d.branch} onChange={(e) => set('branch', e.target.value)} />
          <div className="flex flex-col gap-2">
            <span className="text-caption font-medium text-fg-muted">Runs</span>
            <Segmented
              label="How the session runs by default"
              value={d.mode}
              onChange={(v) => set('mode', v)}
              options={[
                { value: 'headless', label: 'In the background' },
                { value: 'terminal', label: 'In a terminal window' },
              ]}
            />
          </div>
        </div>

        {touched && invalid && (
          <p role="alert" className="rounded-control border border-failed/30 bg-failed-soft px-3 py-2 text-ui text-fg">
            {invalid}
          </p>
        )}
        {preview && (
          <details className="rounded-control border border-border-subtle bg-bg">
            <summary className="cursor-pointer select-none px-3 py-2 text-caption text-fg-muted hover:text-fg">What the agent receives, with example values</summary>
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap border-t border-border-subtle px-3 py-2 font-mono text-caption leading-relaxed text-fg-muted">{preview}</pre>
          </details>
        )}
        {error && <ErrorState error={error} onRetry={save} />}
      </div>
    </Dialog>
  );
}
